// IMF PortWatch (ArcGIS FeatureServer, CORS open): daily transits through 28 chokepoints.
// A chokepoint is reported when its latest day is below the mean of the 30 days before it.
//
//   n_total ─┬─ latest day  ┐
//            └─ prior 30 d ─┴─ loss = 1 − latest / mean   (severity, 0..1)

import { addDays } from '../../core/dates.ts';
import type { Fetcher } from '../types.ts';
import { getJson } from './http.ts';
import type { LiveRecord } from './types.ts';

const BASE = 'https://services9.arcgis.com/weJ1QsnbMYJlCHdG/arcgis/rest/services';
const DAILY = `${BASE}/Daily_Chokepoints_Data/FeatureServer/0/query`;
const META = `${BASE}/PortWatch_chokepoints_database/FeatureServer/0/query`;
const PAGE_URL = 'https://portwatch.imf.org/';
const PUBLISHER = 'IMF PortWatch';
const BASELINE_DAYS = 30;
/** Newest data trails the wall clock by about a week; look this far back for the latest row. */
const LOOKBACK_DAYS = BASELINE_DAYS + 21;
/** Below this loss the chokepoint is running normally and is not reported. */
const MIN_LOSS = 0.25;
const MIN_BASELINE_TRANSITS = 5;

interface Rows<A> {
  features: Array<{ attributes: A }>;
}

interface DailyRow {
  date: string;
  portid: string;
  portname: string;
  n_total: number;
}

interface MetaRow {
  portid: string;
  lat: number;
  lon: number;
}

function query(base: string, params: Record<string, string>): string {
  return `${base}?${new URLSearchParams({ ...params, f: 'json' }).toString()}`;
}

export async function portwatchChokepoints(fetcher: Fetcher, today: string): Promise<LiveRecord[]> {
  const since = addDays(today, -LOOKBACK_DAYS);
  const [daily, meta] = await Promise.all([
    getJson<Rows<DailyRow>>(fetcher, query(DAILY, {
      where: `date >= DATE '${since}'`, outFields: 'date,portid,portname,n_total', orderByFields: 'date DESC',
    })),
    getJson<Rows<MetaRow>>(fetcher, query(META, { where: '1=1', outFields: 'portid,lat,lon' })),
  ]);
  const at = new Map(meta.features.map((f) => [f.attributes.portid, f.attributes]));
  const byPort = new Map<string, DailyRow[]>();

  for (const f of daily.features) {
    const list = byPort.get(f.attributes.portid) ?? [];
    list.push(f.attributes);
    byPort.set(f.attributes.portid, list);
  }

  const out: LiveRecord[] = [];
  for (const [id, rows] of byPort) {
    const [latest, ...prior] = rows;
    const base = prior.slice(0, BASELINE_DAYS);
    const mean = base.reduce((s, r) => s + r.n_total, 0) / Math.max(base.length, 1);
    const loss = 1 - latest.n_total / mean;
    if (mean < MIN_BASELINE_TRANSITS || loss < MIN_LOSS) {
      continue;
    }
    const place = at.get(id);
    out.push({
      id: `portwatch:${id}:${latest.date}`,
      kind: 'chokepoint',
      title: `${latest.portname}: ${latest.n_total} transits on ${latest.date}, ${Math.round(loss * 100)}% below the prior 30-day mean of ${mean.toFixed(1)}`,
      time: `${latest.date}T00:00:00.000Z`,
      lat: place?.lat,
      lon: place?.lon,
      url: PAGE_URL,
      publisher: PUBLISHER,
      severity: loss,
    });
  }
  return out;
}
